import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import {
    AUDIT_TIME_WINDOW_MINUTES,
    type AuditFilterChange,
    type AuditLogEntry,
    addAuditFilters,
    auditTimeWindow,
    describeAuditAction,
    describeAuditOutcome,
    describeAuditTime,
    replaceAuditFilters,
    usableAuditFilter,
    useAuditText,
} from '@/domain/auditLog';

import { OutcomeMark } from '../cells/OutcomeCell';

import DetailsFilterButton from './DetailsFilterButton';

import styles from './DetailsHeader.module.scss';

interface DetailsHeaderProps {
    entry: AuditLogEntry;
    /** Loaded entries sharing the entry's request, itself included. */
    requestCount: number;
    onFilter: (change: AuditFilterChange) => void;
}

/**
 * What happened, when and whether it worked, then the two pivots. The pivots replace
 * every filter: they ask a new question around this entry rather than narrowing the old.
 * Like every other button in the dialog, one whose filter the page would drop is left out.
 */
export default function DetailsHeader({ entry, requestCount, onFilter }: DetailsHeaderProps) {
    const { t } = useTranslation();
    const render = useAuditText();
    const action = describeAuditAction(entry.action);
    const outcome = describeAuditOutcome(entry);
    const outcomeWord = render(outcome.word);
    const actionFilter = usableAuditFilter(action.filter);
    const outcomeFilter = usableAuditFilter(outcome.filter);
    const requestFilter =
        entry.requestId === null ? null : usableAuditFilter({ requestId: entry.requestId });
    const windowFilter = usableAuditFilter(auditTimeWindow(entry.occurredAt));

    return (
        <div className={styles.header}>
            <div className={styles.titleLine}>
                <span className={styles.action}>
                    <span className={styles.area}>{action.area}</span>
                    <span className={styles.name}>{action.name}</span>
                </span>
                {actionFilter === null ? null : (
                    <DetailsFilterButton
                        name={t('auditLog.details.filterValue', {
                            label: t('auditLog.chips.label.actions'),
                            value: entry.action,
                        })}
                        onFilter={() => onFilter(addAuditFilters(actionFilter))}
                    />
                )}
                <span className={styles.outcome}>
                    <OutcomeMark outcome={outcome.outcome}>{render(outcome.label)}</OutcomeMark>
                    {outcomeFilter === null ? null : (
                        <DetailsFilterButton
                            name={t('auditLog.details.filterValue', {
                                label: t('auditLog.chips.label.outcome'),
                                value: outcomeWord,
                            })}
                            onFilter={() => onFilter(addAuditFilters(outcomeFilter))}
                        />
                    )}
                </span>
            </div>
            <p className={styles.time}>{render(describeAuditTime(entry.occurredAt).title)}</p>
            <div className={styles.pivots}>
                {requestFilter === null ? null : (
                    <Button
                        variant="secondary"
                        size="small"
                        className={styles.pivot}
                        onClick={() => onFilter(replaceAuditFilters(requestFilter))}>
                        {/* The count includes this entry, so the copy for one has no number:
                            "(1)" would only say that the entry is alone in its request. */}
                        {t('auditLog.details.showRequest', { count: requestCount })}
                    </Button>
                )}
                {windowFilter === null ? null : (
                    <Button
                        variant="secondary"
                        size="small"
                        className={styles.pivot}
                        onClick={() => onFilter(replaceAuditFilters(windowFilter))}>
                        {t('auditLog.details.showWindow', { count: AUDIT_TIME_WINDOW_MINUTES })}
                    </Button>
                )}
            </div>
        </div>
    );
}
